import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useMap } from '../map/MapContext';
import { useOgcServices } from '../services/OgcServicesContext';
import { ElevationSampler, type ElevationSample, type RoutePoint } from '../services/elevation-sampler';
import type { GeoJSONSource } from 'maplibre-gl';

const ROUTE_SOURCE = 'terrain-profile-route';
const CURSOR_SOURCE = 'terrain-profile-cursor';
const WAYPOINT_SOURCE = 'terrain-profile-waypoints';
const NUM_SAMPLES = 200;

export const TerrainProfilePanel: React.FC = () => {
  const { map } = useMap();
  const { tileEndpoints } = useOgcServices();
  const [route, setRoute] = useState<RoutePoint[]>([]);
  const [samples, setSamples] = useState<ElevationSample[]>([]);
  const [loading, setLoading] = useState(false);
  const [cursorIdx, setCursorIdx] = useState<number | null>(null);
  const [clearanceAlt, setClearanceAlt] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const sampler = useMemo(
    () => new ElevationSampler(tileEndpoints.terrainDem, 11),
    [tileEndpoints.terrainDem],
  );

  const stats = useMemo(() => ElevationSampler.computeStats(samples), [samples]);

  // ─── Map layers for route and cursor ───────────────────────────────────────
  useEffect(() => {
    if (!map) return;
    // Route line
    if (!map.getSource(ROUTE_SOURCE)) {
      map.addSource(ROUTE_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({ id: `${ROUTE_SOURCE}-line`, type: 'line', source: ROUTE_SOURCE, paint: { 'line-color': '#22C55E', 'line-width': 3 } });
    }
    // Waypoint circles
    if (!map.getSource(WAYPOINT_SOURCE)) {
      map.addSource(WAYPOINT_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({ id: `${WAYPOINT_SOURCE}-circle`, type: 'circle', source: WAYPOINT_SOURCE, paint: { 'circle-radius': 6, 'circle-color': '#22C55E', 'circle-stroke-width': 2, 'circle-stroke-color': '#0F172A' } });
      map.addLayer({ id: `${WAYPOINT_SOURCE}-label`, type: 'symbol', source: WAYPOINT_SOURCE, layout: { 'text-field': ['get', 'label'], 'text-size': 11, 'text-offset': [0, 1.4], 'text-anchor': 'top', 'text-font': ['Open Sans Regular'] }, paint: { 'text-color': '#F1F5F9', 'text-halo-color': '#0F172A', 'text-halo-width': 1 } });
    }
    // Cursor marker
    if (!map.getSource(CURSOR_SOURCE)) {
      map.addSource(CURSOR_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({ id: `${CURSOR_SOURCE}-circle`, type: 'circle', source: CURSOR_SOURCE, paint: { 'circle-radius': 8, 'circle-color': '#3B82F6', 'circle-stroke-width': 2, 'circle-stroke-color': '#fff' } });
    }
    return () => {
      for (const id of [`${ROUTE_SOURCE}-line`, `${WAYPOINT_SOURCE}-circle`, `${WAYPOINT_SOURCE}-label`, `${CURSOR_SOURCE}-circle`]) {
        try { map.removeLayer(id); } catch { /* ok */ }
      }
      for (const id of [ROUTE_SOURCE, WAYPOINT_SOURCE, CURSOR_SOURCE]) {
        try { map.removeSource(id); } catch { /* ok */ }
      }
    };
  }, [map]);

  // Sync route line + waypoints to map
  useEffect(() => {
    if (!map) return;
    const routeSrc = map.getSource(ROUTE_SOURCE) as GeoJSONSource | undefined;
    const wayptSrc = map.getSource(WAYPOINT_SOURCE) as GeoJSONSource | undefined;
    if (routeSrc && route.length >= 2) {
      routeSrc.setData({ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: route.map((p) => [p.lng, p.lat]) } });
    } else if (routeSrc) {
      routeSrc.setData({ type: 'FeatureCollection', features: [] });
    }
    if (wayptSrc) {
      wayptSrc.setData({
        type: 'FeatureCollection',
        features: route.map((p, i) => ({
          type: 'Feature' as const, properties: { label: p.label ?? `WPT ${i + 1}` },
          geometry: { type: 'Point' as const, coordinates: [p.lng, p.lat] },
        })),
      });
    }
  }, [map, route]);

  // Sync cursor position to map
  useEffect(() => {
    if (!map || cursorIdx == null || !samples[cursorIdx]) return;
    const s = samples[cursorIdx];
    const src = map.getSource(CURSOR_SOURCE) as GeoJSONSource | undefined;
    if (src) {
      src.setData({
        type: 'Feature', properties: {},
        geometry: { type: 'Point', coordinates: [s.longitude, s.latitude] },
      });
    }
  }, [map, cursorIdx, samples]);

  // ─── Build profile from route using real DEM ───────────────────────────────
  const buildProfile = useCallback(async () => {
    if (route.length < 2) return;
    setLoading(true);
    setCursorIdx(null);
    try {
      const result = await sampler.sampleRoute(route, NUM_SAMPLES);
      setSamples(result);
    } catch (err) {
      console.error('Elevation sampling failed:', err);
      setSamples([]);
    } finally {
      setLoading(false);
    }
  }, [route, sampler]);

  // ─── Draw chart ────────────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || samples.length === 0) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const W = canvas.clientWidth * dpr;
    const H = canvas.clientHeight * dpr;
    canvas.width = W;
    canvas.height = H;
    ctx.scale(dpr, dpr);
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;

    const maxElev = Math.max(stats.maxFt * 1.15, 1000);
    const maxDist = stats.totalDistNm || 1;
    const clearanceFt = clearanceAlt ? parseInt(clearanceAlt, 10) : null;

    ctx.clearRect(0, 0, w, h);

    // Grid lines + altitude labels
    ctx.strokeStyle = '#334155';
    ctx.lineWidth = 0.5;
    ctx.fillStyle = '#64748B';
    ctx.font = '9px sans-serif';
    ctx.textAlign = 'left';
    for (let i = 0; i <= 4; i++) {
      const y = h - (i / 4) * h;
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke();
      const alt = ((i / 4) * maxElev).toFixed(0);
      ctx.fillText(`${alt}'`, 2, y - 2);
    }

    // Clearance altitude line
    if (clearanceFt != null && clearanceFt > 0) {
      const clrY = h - (clearanceFt / maxElev) * h;
      ctx.strokeStyle = '#EF4444';
      ctx.lineWidth = 1;
      ctx.setLineDash([6, 4]);
      ctx.beginPath(); ctx.moveTo(0, clrY); ctx.lineTo(w, clrY); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = '#EF4444';
      ctx.fillText(`${clearanceFt}' clearance`, 2, clrY - 3);
    }

    // Terrain fill
    ctx.beginPath();
    ctx.moveTo(0, h);
    for (const s of samples) {
      ctx.lineTo((s.distanceNm / maxDist) * w, h - (s.elevationFt / maxElev) * h);
    }
    ctx.lineTo(w, h);
    ctx.closePath();
    const gradient = ctx.createLinearGradient(0, 0, 0, h);
    gradient.addColorStop(0, 'rgba(34, 197, 94, 0.5)');
    gradient.addColorStop(1, 'rgba(34, 197, 94, 0.05)');
    ctx.fillStyle = gradient;
    ctx.fill();

    // Terrain line
    ctx.beginPath();
    ctx.strokeStyle = '#22C55E';
    ctx.lineWidth = 1.5;
    for (let i = 0; i < samples.length; i++) {
      const x = (samples[i].distanceNm / maxDist) * w;
      const y = h - (samples[i].elevationFt / maxElev) * h;
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.stroke();

    // Cursor
    if (cursorIdx != null && cursorIdx < samples.length) {
      const s = samples[cursorIdx];
      const x = (s.distanceNm / maxDist) * w;
      const y = h - (s.elevationFt / maxElev) * h;
      // Vertical line
      ctx.strokeStyle = 'rgba(59,130,246,0.4)';
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke();
      // Dot
      ctx.beginPath(); ctx.arc(x, y, 5, 0, Math.PI * 2);
      ctx.fillStyle = '#3B82F6'; ctx.fill();
      ctx.strokeStyle = '#fff'; ctx.lineWidth = 1.5; ctx.stroke();
      // Label
      ctx.fillStyle = '#F1F5F9'; ctx.font = 'bold 11px sans-serif';
      const label = `${s.elevationFt.toFixed(0)} ft  ·  ${s.distanceNm.toFixed(1)} NM`;
      const tx = x + 8 > w - 80 ? x - 8 : x + 8;
      ctx.textAlign = tx === x + 8 ? 'left' : 'right';
      ctx.fillText(label, tx, y - 8);
    }
  }, [samples, cursorIdx, stats, clearanceAlt]);

  const handleCanvasHover = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || samples.length === 0) return;
    const rect = canvas.getBoundingClientRect();
    const frac = (e.clientX - rect.left) / rect.width;
    setCursorIdx(Math.min(samples.length - 1, Math.max(0, Math.round(frac * (samples.length - 1)))));
  }, [samples]);

  // ─── Route editing (right-click on map to add waypoints) ───────────────────
  useEffect(() => {
    if (!map) return;
    const onClick = (e: any) => {
      const label = `WPT ${route.length + 1}`;
      setRoute((prev) => [...prev, { lat: e.lngLat.lat, lng: e.lngLat.lng, label }]);
    };
    map.on('contextmenu', onClick);
    return () => { map.off('contextmenu', onClick); };
  }, [map, route.length]);

  const undoWaypoint = useCallback(() => {
    setRoute((prev) => prev.slice(0, -1));
  }, []);

  const clearRoute = useCallback(() => {
    setRoute([]);
    setSamples([]);
    setCursorIdx(null);
    sampler.clearCache();
  }, [sampler]);

  return (
    <div>
      {/* Route controls */}
      <div className="panel-section">
        <div className="panel-section-title">Route ({route.length} waypoints)</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
          Right-click the map to add waypoints, then generate the terrain profile from DEM tiles.
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-primary" onClick={buildProfile} disabled={route.length < 2 || loading}>
            {loading ? '⏳ Sampling DEM…' : '⛰️ Generate Profile'}
          </button>
          <button className="btn btn-secondary" onClick={undoWaypoint} disabled={route.length === 0}>↩</button>
          <button className="btn btn-secondary" onClick={clearRoute} disabled={route.length === 0}>🗑️</button>
        </div>
      </div>

      {/* Waypoint list */}
      {route.length > 0 && (
        <div className="panel-section">
          <div className="panel-section-title">Waypoints</div>
          {route.map((p, i) => (
            <div key={i} className="list-item" style={{ padding: '3px 6px' }}>
              <span style={{ fontWeight: 600, fontSize: 11, color: 'var(--accent-light)', minWidth: 50 }}>{p.label ?? `WPT ${i + 1}`}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)' }}>
                {p.lat.toFixed(5)}°, {p.lng.toFixed(5)}°
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Chart */}
      {samples.length > 0 && (
        <div className="panel-section">
          <div className="panel-section-title">Elevation Profile</div>
          <canvas
            ref={canvasRef}
            onMouseMove={handleCanvasHover}
            onMouseLeave={() => setCursorIdx(null)}
            style={{ width: '100%', height: 160, background: 'var(--bg-input)', borderRadius: 6, cursor: 'crosshair' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
            <span>0 NM</span>
            <span>{stats.totalDistNm.toFixed(1)} NM</span>
          </div>
        </div>
      )}

      {/* Clearance altitude */}
      {samples.length > 0 && (
        <div className="panel-section">
          <div className="panel-section-title">Clearance Altitude</div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              className="input" type="number" placeholder="ft MSL"
              value={clearanceAlt} onChange={(e) => setClearanceAlt(e.target.value)}
              style={{ width: 100 }}
            />
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>ft MSL</span>
          </div>
        </div>
      )}

      {/* Stats */}
      {samples.length > 0 && (
        <div className="panel-section">
          <div className="panel-section-title">Terrain Stats</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {[
              { label: 'Max Elevation', value: `${stats.maxFt.toFixed(0)} ft` },
              { label: 'Min Elevation', value: `${stats.minFt.toFixed(0)} ft` },
              { label: 'Avg Elevation', value: `${stats.avgFt.toFixed(0)} ft` },
              { label: 'Total Climb', value: `${stats.totalClimbFt.toFixed(0)} ft` },
              { label: 'Total Descent', value: `${stats.totalDescentFt.toFixed(0)} ft` },
              { label: 'Distance', value: `${stats.totalDistNm.toFixed(1)} NM` },
            ].map(({ label, value }) => (
              <div key={label} style={{ background: 'var(--bg-input)', borderRadius: 6, padding: '6px 8px' }}>
                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{label}</div>
                <div style={{ fontSize: 14, fontWeight: 600, fontFamily: 'var(--font-mono)' }}>{value}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
